// src/components/UpgradeModal.js
// 制限に当たった瞬間に出る。文脈オンボーディング（EmptyState）と同じ思想で、
// 「困った瞬間にだけ、何が解けるかを1枚で示す」。
import React from 'react';
import { useTheme } from '../lib/ThemeContext';
import { IconClose, IconCheck, IconStar } from '../lib/icons';
import { UPGRADE_COPY, PRO_FEATURES, PRO_PRICE_LABEL } from '../lib/plans';
import { LEGAL_LINKS } from '../lib/legalContent';

export default function UpgradeModal({ open, reason = 'default', loading, onUpgrade, onClose }) {
  const { theme } = useTheme();
  if (!open) return null;

  const copy = UPGRADE_COPY[reason] || UPGRADE_COPY.default;

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '16px',
        zIndex: 100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: '400px',
          background: theme.bgCard,
          border: `1px solid ${theme.border}`,
          borderRadius: '16px',
          boxShadow: theme.shadow,
          overflow: 'hidden',
        }}
      >
        {/* ヘッダー */}
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            padding: '22px 22px 0',
          }}
        >
          <div
            style={{
              width: '38px',
              height: '38px',
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: theme.bgInput,
              border: `1px solid ${theme.border}`,
              flexShrink: 0,
            }}
          >
            <IconStar size={19} color={theme.accent} />
          </div>
          <button
            onClick={onClose}
            aria-label="閉じる"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              lineHeight: 0,
            }}
          >
            <IconClose size={18} color={theme.textWeak} />
          </button>
        </div>

        {/* 本文 */}
        <div style={{ padding: '14px 22px 0' }}>
          <div
            style={{
              fontSize: '17px',
              fontWeight: 700,
              color: theme.textPrimary,
              lineHeight: 1.45,
            }}
          >
            {copy.title}
          </div>
          <div
            style={{
              marginTop: '8px',
              fontSize: '13px',
              color: theme.textMuted,
              lineHeight: 1.75,
            }}
          >
            {copy.desc}
          </div>

          {/* Proでできること */}
          <div
            style={{
              marginTop: '18px',
              padding: '14px 16px',
              background: theme.bgInput,
              border: `1px solid ${theme.border}`,
              borderRadius: '12px',
            }}
          >
            <div
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                color: theme.textWeak,
                marginBottom: '10px',
              }}
            >
              PROプランでできること
            </div>
            {PRO_FEATURES.map((f) => (
              <div
                key={f}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '4px 0',
                  fontSize: '13px',
                  color: theme.textPrimary,
                }}
              >
                <IconCheck size={15} color={theme.accent} />
                <span>{f}</span>
              </div>
            ))}
          </div>

          <div
            style={{
              marginTop: '16px',
              textAlign: 'center',
              fontSize: '20px',
              fontWeight: 800,
              color: theme.textPrimary,
              letterSpacing: '-0.01em',
            }}
          >
            {PRO_PRICE_LABEL}
          </div>
          <div
            style={{
              marginTop: '4px',
              textAlign: 'center',
              fontSize: '11px',
              color: theme.textWeak,
            }}
          >
            いつでも解約できます
          </div>
        </div>

        {/* フッター */}
        <div style={{ padding: '18px 22px 22px' }}>
          <button
            onClick={onUpgrade}
            disabled={loading}
            style={{
              width: '100%',
              padding: '13px',
              borderRadius: '12px',
              border: 'none',
              background: loading ? theme.bgHover : theme.primary,
              color: loading ? theme.textWeak : theme.primaryText,
              fontSize: '14px',
              fontWeight: 700,
              cursor: loading ? 'default' : 'pointer',
            }}
          >
            {loading ? '決済ページを開いています…' : 'Proにアップグレード'}
          </button>
          <button
            onClick={onClose}
            style={{
              width: '100%',
              marginTop: '8px',
              padding: '11px',
              borderRadius: '12px',
              border: 'none',
              background: 'transparent',
              color: theme.textWeak,
              fontSize: '13px',
              cursor: 'pointer',
            }}
          >
            あとで
          </button>

          {/* 法務リンク（Stripe審査・特商法の表示義務）
              PlanProvider は Router の外側で描画されるため useNavigate は使えない。
              新規タブで開く＝決済フローの文脈を失わせない、というUX上の利点もある */}
          <div style={{
            marginTop: '14px',
            paddingTop: '12px',
            borderTop: `1px solid ${theme.border}`,
            display: 'flex',
            justifyContent: 'center',
            flexWrap: 'wrap',
            gap: '4px 12px',
          }}>
            {LEGAL_LINKS.map(l => (
              <a
                key={l.key}
                href={l.path}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '11px',
                  color: theme.textWeak,
                  textDecoration: 'underline',
                  textUnderlineOffset: '3px',
                }}
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
